/**
 * RetargetCampaignModal.jsx — §4 Duplicate & Retarget modal.
 *
 * Opens from any campaign card. Walks the user through:
 *   A. Original campaign performance summary + AI insight
 *   B. Retarget lead selection (5 radio cards, default = no-response + no-conversion)
 *   C. Campaign settings (name, script, agents, cadence, wait-period, daily target)
 *   D. Advanced options (collapsible)
 * Footer: live "[N] leads will be targeted", Cancel / Save as draft / Launch.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

const TILE = { background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" };
const LABEL = { fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 5 };
const INPUT = { width: "100%", border: "0.5px solid #dddddd", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

function pct(part, total) {
  if (!total) return "0%";
  return `${Math.round((part * 100) / total)}%`;
}

const RADIO_OPTIONS = [
  { id: "no_response",     title: "No response — never connected",                  description: "Leads that never picked up — no live conversation logged." },
  { id: "contacted_no_conv", title: "Contacted but not converted",                  description: "Reached, talked — did not convert to demo / quote / contract." },
  { id: "no_resp_no_conv", title: "No response + Not converted",                    description: "Combined audience — leads that either never picked up or didn't move forward.", recommended: true },
  { id: "voicemail_only",  title: "Voicemail only — 2 or more attempts",            description: "Hit voicemail twice or more without a return call." },
  { id: "custom",          title: "Custom — build my own filter",                   description: "Pick filter conditions explicitly (mirror of New Campaign Step 2)." },
];

function defaultRetargetName(name, existing = 0) {
  const base = (name || "Campaign").replace(/—\s*Retarget R\d+\s*$/i, "").trim();
  return `${base} — Retarget R${existing + 1}`;
}

function buildPerformance(campaign, leadCount) {
  // Use campaign metrics if available; fall back to deterministic synthesis
  // so the modal renders meaningful numbers even on demo data.
  const total = leadCount || campaign.total_leads || 1000;
  const contacted = campaign.contacted ?? Math.round(total * 0.62);
  const connected = campaign.connected ?? Math.round(total * 0.27);
  const noResp = campaign.no_response ?? Math.max(0, total - contacted);
  const meetings = campaign.meetings_booked ?? Math.round(connected * 0.18);
  const conv = total > 0 ? (meetings * 100) / total : 0;
  return { total, contacted, connected, noResp, meetings, conv };
}

function buildAiInsight(perf, campaign) {
  // Static, deterministic insight — Azure OpenAI hookup is opt-in (passed
  // via `aiInsightFn` prop). Falls back to a template that reads the
  // numbers above so demos always have meaningful copy.
  const n = perf.noResp + Math.max(0, perf.contacted - perf.connected - perf.meetings);
  const conv = perf.conv.toFixed(1);
  if (perf.conv < 5) {
    return `${n} leads from "${campaign.name || "this campaign"}" went silent — conversion is at ${conv}%. Try a fresh angle (cost or AI features) on a tighter cadence to lift response.`;
  }
  if (perf.conv > 10) {
    return `Conversion is healthy (${conv}%) — the ${n} unresponded leads are the highest-leverage retarget audience. A short, urgency-driven cadence should outperform a generic re-touch.`;
  }
  return `${n} leads went without a meaningful response. Conversion is ${conv}% — average; a sharper messaging angle on the retarget can move the bar.`;
}

export default function RetargetCampaignModal({
  open, parentCampaign, parentLeadCount = 1200, scripts = [], agents = [],
  existingRetargetCount = 0,
  onCancel, onSaveDraft, onLaunch,
}) {
  const perf = useMemo(() => buildPerformance(parentCampaign || {}, parentLeadCount), [parentCampaign, parentLeadCount]);
  const aiInsight = useMemo(() => buildAiInsight(perf, parentCampaign || {}), [perf, parentCampaign]);

  const [selection, setSelection] = useState("no_resp_no_conv");
  const [customSelected, setCustomSelected] = useState(false);
  const [name, setName] = useState("");
  const [scriptId, setScriptId] = useState("");
  const [agentIds, setAgentIds] = useState([]);
  const [cadence, setCadence] = useState("standard"); // standard | aggressive | persistent
  const [waitPeriod, setWaitPeriod] = useState("3d");  // immediate | 3d | 1w | 2w
  const [dailyTarget, setDailyTarget] = useState(100);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [excludeRecent, setExcludeRecent] = useState(true);
  const [excludeDays, setExcludeDays] = useState(7);
  const [angle, setAngle] = useState("same");
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [autoArchive, setAutoArchive] = useState(false);

  useEffect(() => {
    if (!parentCampaign) return;
    setName(defaultRetargetName(parentCampaign.name, existingRetargetCount));
    setScriptId(parentCampaign.script_id || "");
    setAgentIds(Array.isArray(parentCampaign.agent_ids) ? [...parentCampaign.agent_ids] : []);
  }, [parentCampaign, existingRetargetCount]);

  if (!open || !parentCampaign) return null;

  const selectionCount = (() => {
    switch (selection) {
      case "no_response":     return perf.noResp;
      case "contacted_no_conv": return Math.max(0, perf.contacted - perf.meetings);
      case "no_resp_no_conv": return Math.max(0, perf.total - perf.meetings);
      case "voicemail_only":  return Math.round(perf.noResp * 0.55);
      case "custom":          return Math.round(perf.total * 0.4);
      default:                return 0;
    }
  })();

  const submit = (mode) => {
    const payload = {
      parent_campaign_id: parentCampaign.id,
      parent_campaign_name: parentCampaign.name,
      name,
      script_id: scriptId,
      agent_ids: agentIds,
      cadence, wait_period: waitPeriod, daily_target_per_agent: dailyTarget,
      audience: selection,
      advanced: { exclude_recent: excludeRecent, exclude_days: excludeDays, angle, max_attempts: maxAttempts, auto_archive: autoArchive },
      target_count: selectionCount,
      mode,
    };
    if (mode === "draft") onSaveDraft?.(payload);
    else onLaunch?.(payload);
  };

  return (
    <div data-testid="retarget-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 680, maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 14, padding: "18px 20px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 500 }}>Duplicate & Retarget — {parentCampaign.name || "Campaign"}</span>
          <button data-testid="retarget-close" onClick={onCancel} style={{ background: "none", border: "none", color: "#888", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#888", paddingBottom: 12, borderBottom: "0.5px solid #eee" }}>
          Create a new campaign based on this one, targeting leads that need follow-up. Review the performance, choose which leads to retarget, and adjust settings before launching.
        </div>

        {/* Section A — performance */}
        <div style={{ paddingTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>Original campaign performance</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div data-testid="perf-tile-total" style={TILE}><div style={{ fontSize: 10, color: "#888" }}>Total leads</div><div style={{ fontSize: 18, fontWeight: 600 }}>{perf.total.toLocaleString()}</div></div>
            <div data-testid="perf-tile-contacted" style={TILE}><div style={{ fontSize: 10, color: "#888" }}>Contacted</div><div style={{ fontSize: 18, fontWeight: 600 }}>{perf.contacted.toLocaleString()} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>({pct(perf.contacted, perf.total)})</span></div></div>
            <div data-testid="perf-tile-connected" style={TILE}><div style={{ fontSize: 10, color: "#888" }}>Connected</div><div style={{ fontSize: 18, fontWeight: 600 }}>{perf.connected.toLocaleString()} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>({pct(perf.connected, perf.total)})</span></div></div>
            <div data-testid="perf-tile-noresp" style={{ ...TILE, borderLeft: "3px solid #BA7517" }}><div style={{ fontSize: 10, color: "#888" }}>No response</div><div style={{ fontSize: 18, fontWeight: 600 }}>{perf.noResp.toLocaleString()} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>({pct(perf.noResp, perf.total)})</span></div></div>
            <div data-testid="perf-tile-meetings" style={TILE}><div style={{ fontSize: 10, color: "#888" }}>Meetings booked</div><div style={{ fontSize: 18, fontWeight: 600 }}>{perf.meetings.toLocaleString()} <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>({pct(perf.meetings, perf.total)})</span></div></div>
            <div data-testid="perf-tile-conv" style={TILE}><div style={{ fontSize: 10, color: "#888" }}>Conversion rate</div><div style={{ fontSize: 18, fontWeight: 600, color: perf.conv < 5 ? "#BA7517" : perf.conv > 10 ? "#0F6E56" : "#222" }}>{perf.conv.toFixed(1)}%</div></div>
          </div>
          <div data-testid="retarget-ai-insight" style={{ background: "#EEEDFE", borderRadius: 8, padding: "8px 12px", marginTop: 8, color: "#3C3489", fontSize: 12, display: "flex", alignItems: "flex-start", gap: 6 }}>
            <Sparkles style={{ width: 12, height: 12, marginTop: 2 }} />
            <span>{aiInsight}</span>
          </div>
        </div>

        {/* Section B — selection */}
        <div style={{ paddingTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Which leads should this retarget campaign include?</div>
          {RADIO_OPTIONS.map((opt) => {
            const selected = selection === opt.id;
            return (
              <div
                key={opt.id}
                data-testid={`retarget-option-${opt.id}`}
                onClick={() => { setSelection(opt.id); if (opt.id === "custom") setCustomSelected(true); else setCustomSelected(false); }}
                style={{
                  border: selected ? "1.5px solid #2563eb" : "0.5px solid #eeeeee",
                  background: selected ? "#f0f7ff" : "#fff",
                  borderRadius: 8, padding: "10px 14px", marginBottom: 6,
                  display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
                }}
              >
                <span style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${selected ? "#2563eb" : "#bbb"}`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 2 }}>
                  {selected && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2563eb" }} />}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{opt.title}</span>
                    {opt.recommended && (
                      <span style={{ background: "#EAF3DE", color: "#3B6D11", fontSize: 10, padding: "1px 6px", borderRadius: 10 }}>✦ Recommended</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{opt.description}</div>
                </div>
                <span style={{ fontSize: 11, color: "#444" }}>
                  {opt.id === "no_response" && `${perf.noResp.toLocaleString()} leads`}
                  {opt.id === "contacted_no_conv" && `${Math.max(0, perf.contacted - perf.meetings).toLocaleString()} leads`}
                  {opt.id === "no_resp_no_conv" && `${Math.max(0, perf.total - perf.meetings).toLocaleString()} leads`}
                  {opt.id === "voicemail_only" && `${Math.round(perf.noResp * 0.55).toLocaleString()} leads`}
                  {opt.id === "custom" && "Build filter →"}
                </span>
              </div>
            );
          })}
          {customSelected && (
            <div data-testid="retarget-custom-filter-stub" style={{ marginTop: 6, padding: "10px 12px", background: "#f9f9f9", border: "0.5px solid #eee", borderRadius: 8, fontSize: 11, color: "#666" }}>
              Custom audience filters — uses the same filter set as New Campaign Step 2 (state, city, ZIP, source, specialty, products, budget, timeline, EHR, rating, practice type, region).
            </div>
          )}
          <div data-testid="retarget-live-count" style={{ marginTop: 10, fontSize: 14, fontWeight: 500, color: "#185FA5" }}>
            {selectionCount.toLocaleString()} leads will be included in this retarget campaign
          </div>
        </div>

        {/* Section C — settings */}
        <div style={{ paddingTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>Campaign settings</div>
          <div style={{ marginBottom: 10 }}>
            <label style={LABEL}>Campaign name</label>
            <input data-testid="retarget-name" value={name} onChange={(e) => setName(e.target.value)} style={INPUT} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={LABEL}>Call script</label>
              <select data-testid="retarget-script" value={scriptId} onChange={(e) => setScriptId(e.target.value)} style={INPUT}>
                <option value="">— Select script —</option>
                {scripts.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
            <div>
              <label style={LABEL}>Agents</label>
              <select
                data-testid="retarget-add-agent"
                onChange={(e) => { if (e.target.value && !agentIds.includes(e.target.value)) setAgentIds([...agentIds, e.target.value]); e.target.value = ""; }}
                style={INPUT}
              >
                <option value="">+ Add agent…</option>
                {agents.filter((a) => !agentIds.includes(a.id)).map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {agentIds.map((aid) => {
                  const a = agents.find((x) => x.id === aid);
                  return (
                    <span key={aid} style={{ background: "#E6F1FB", color: "#185FA5", borderRadius: 10, padding: "1px 8px", fontSize: 11, display: "inline-flex", gap: 4 }}>
                      {a?.name || aid}
                      <button onClick={() => setAgentIds(agentIds.filter((x) => x !== aid))} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0 }}>×</button>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={LABEL}>Call cadence</label>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>How often should agents attempt to reach each lead per day?</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "standard", label: "Standard (1 attempt/day)" },
                { id: "aggressive", label: "Aggressive (2 attempts/day)" },
                { id: "persistent", label: "Persistent (3 attempts/day)" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  data-testid={`retarget-cadence-${opt.id}`}
                  onClick={() => setCadence(opt.id)}
                  style={{
                    border: cadence === opt.id ? "1.5px solid #2563eb" : "0.5px solid #ddd",
                    background: cadence === opt.id ? "#f0f7ff" : "#fff",
                    color: cadence === opt.id ? "#185FA5" : "#444",
                    borderRadius: 999, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                  }}
                >{opt.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={LABEL}>Start calling</label>
              <select data-testid="retarget-wait" value={waitPeriod} onChange={(e) => setWaitPeriod(e.target.value)} style={INPUT}>
                <option value="immediately">Immediately</option>
                <option value="3d">After 3 days (default)</option>
                <option value="1w">After 1 week</option>
                <option value="2w">After 2 weeks</option>
              </select>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>A short gap gives recently contacted leads a brief break before retargeting.</div>
            </div>
            <div>
              <label style={LABEL}>Daily call target</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input data-testid="retarget-daily-target" type="number" value={dailyTarget} onChange={(e) => setDailyTarget(Number(e.target.value || 0))} style={{ ...INPUT, width: 80 }} />
                <span style={{ fontSize: 12, color: "#888" }}>calls/day</span>
              </div>
            </div>
          </div>
        </div>

        {/* Section D — advanced */}
        <div style={{ paddingTop: 16 }}>
          <button
            data-testid="retarget-advanced-toggle"
            onClick={() => setAdvancedOpen((v) => !v)}
            style={{ background: "none", border: "none", padding: 0, fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            Advanced options {advancedOpen ? <ChevronUp style={{ width: 12, height: 12 }} /> : <ChevronDown style={{ width: 12, height: 12 }} />}
          </button>
          {advancedOpen && (
            <div data-testid="retarget-advanced-body" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input data-testid="retarget-exclude-recent" type="checkbox" checked={excludeRecent} onChange={(e) => setExcludeRecent(e.target.checked)} />
                <span style={{ fontSize: 12 }}>Exclude leads that had any CRM activity in the last</span>
                <input type="number" value={excludeDays} onChange={(e) => setExcludeDays(Number(e.target.value || 0))} style={{ ...INPUT, width: 60, padding: "4px 8px", fontSize: 12 }} />
                <span style={{ fontSize: 12 }}>days.</span>
              </div>
              <div>
                <label style={LABEL}>Messaging angle</label>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Changing the angle will suggest a matching script if one exists.</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    { id: "cost", label: "Cost savings" },
                    { id: "ai", label: "AI features" },
                    { id: "outcomes", label: "Patient outcomes" },
                    { id: "awareness", label: "Product awareness" },
                    { id: "same", label: "Same as original (default)" },
                  ].map((a) => (
                    <button
                      key={a.id}
                      data-testid={`retarget-angle-${a.id}`}
                      onClick={() => setAngle(a.id)}
                      style={{ border: angle === a.id ? "1.5px solid #2563eb" : "0.5px solid #ddd", background: angle === a.id ? "#f0f7ff" : "#fff", color: angle === a.id ? "#185FA5" : "#444", borderRadius: 999, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
                    >{a.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={LABEL}>Max attempts per lead before auto-removing from queue</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input data-testid="retarget-max-attempts" type="number" value={maxAttempts} onChange={(e) => setMaxAttempts(Number(e.target.value || 0))} style={{ ...INPUT, width: 80, padding: "4px 8px", fontSize: 12 }} />
                  <span style={{ fontSize: 12, color: "#888" }}>attempts</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input data-testid="retarget-auto-archive" type="checkbox" checked={autoArchive} onChange={(e) => setAutoArchive(e.target.checked)} />
                <span style={{ fontSize: 12 }}>Automatically archive this campaign after all leads have been attempted.</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "0.5px solid #eee", paddingTop: 16, marginTop: 16, gap: 12 }}>
          <span data-testid="retarget-footer-count" style={{ fontSize: 13, color: "#185FA5" }}>{selectionCount.toLocaleString()} leads will be targeted</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="retarget-cancel" onClick={onCancel} style={{ background: "#fff", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>Cancel</button>
            <button data-testid="retarget-save-draft" onClick={() => submit("draft")} style={{ background: "#fff", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>Save as draft</button>
            <button data-testid="retarget-launch" onClick={() => submit("launch")} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 20px", fontSize: 13, fontFamily: "inherit", cursor: "pointer", fontWeight: 500 }}>Launch retarget campaign ✓</button>
          </div>
        </div>
      </div>
    </div>
  );
}
